import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    BrainCircuit, Clock, ShieldAlert, CheckCircle2, User2, Loader2,
    Timer, AlertTriangle, TrendingUp, Sparkles, DollarSign, Activity, Zap,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ROLES } from "../../lib/roles";
import { Button } from "../../components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const PRIO_STYLE = {
    alta: { badge: "badge-error", label: "Alta", dot: "bg-[color:var(--brand-coral)]" },
    media: { badge: "badge-gold", label: "Media", dot: "bg-[color:var(--brand-gold)]" },
    baja: { badge: "badge-mock", label: "Baja", dot: "bg-muted-foreground" },
};
const ESTADO_STYLE = {
    pendiente: { badge: "badge-gold", icon: Clock, label: "Pendiente" },
    en_revision: { badge: "badge-mock", icon: User2, label: "En revisión" },
    resuelto: { badge: "badge-exito", icon: CheckCircle2, label: "Resuelto" },
    rechazado: { badge: "badge-error", icon: ShieldAlert, label: "Rechazado" },
};
const TIPO_REV_LABEL = {
    baja_confianza: "Baja confianza",
    alto_stakes: "Alto stakes",
    error_llm: "Error LLM",
    flag_regulatorio: "Flag PEP/PLD",
};

function hoursAgo(iso) {
    if (!iso) return 0;
    return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}

export default function ColaHIL() {
    const { hasRole } = useAuth();
    const [params, setParams] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [usage, setUsage] = useState(null);

    const filtroEstado = params.get("estado") || "pendiente";
    const filtroPrioridad = params.get("prioridad") || "";
    const filtroTipo = params.get("tipo_revision") || "";

    const canSeeUsage = hasRole([ROLES.ADMIN_TI, ROLES.DIRECCION, ROLES.AUDITOR]);

    const load = async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            if (filtroEstado) q.set("estado", filtroEstado);
            if (filtroPrioridad) q.set("prioridad", filtroPrioridad);
            if (filtroTipo) q.set("tipo_revision", filtroTipo);
            const { data } = await api.get(`/hil-queue?${q.toString()}`);
            setRows(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtroEstado, filtroPrioridad, filtroTipo]);

    useEffect(() => {
        if (!canSeeUsage) return;
        api.get("/tenants/current/ai-usage").then(({ data }) => setUsage(data)).catch(() => {});
    }, [canSeeUsage]);

    const set = (k, v) => {
        const p = new URLSearchParams(params);
        if (v) p.set(k, v); else p.delete(k);
        setParams(p);
    };

    const kpis = useMemo(() => {
        const k = { pendiente: 0, en_revision: 0, alta: 0, sla_excedido: 0 };
        for (const r of rows) {
            if (r.estado === "pendiente") k.pendiente++;
            if (r.estado === "en_revision") k.en_revision++;
            if (r.prioridad === "alta") k.alta++;
            if (hoursAgo(r.fecha_creacion) > r.sla_horas && r.estado !== "resuelto") k.sla_excedido++;
        }
        return k;
    }, [rows]);

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="cola-hil-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2 flex items-center gap-2">
                    <Sparkles className="h-3 w-3" /> IA · Verificación documental
                </div>
                <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                    <BrainCircuit className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                    Cola de revisión humana
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Documentos KYC procesados con LLM real (Claude Sonnet 4.6) que requieren
                    revisión humana por confianza baja, alto-stakes o match en listas negras.
                </p>
            </div>

            {/* AI Usage widget */}
            {canSeeUsage && usage && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ai-usage-widget">
                    <div className="kpi-card">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                            <DollarSign className="h-3 w-3" /> Usado hoy
                        </div>
                        <div className="font-display text-2xl mt-1 tabular">
                            ${usage.usado_hoy_usd.toFixed(4)}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                            de ${usage.limit_daily_usd.toFixed(2)} límite diario
                        </div>
                        <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div
                                className="h-full bg-[color:var(--brand-dark-gold)]"
                                style={{
                                    width: `${Math.min(100, (usage.usado_hoy_usd / (usage.limit_daily_usd || 1)) * 100)}%`,
                                }}
                            />
                        </div>
                    </div>
                    <div className="kpi-card">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                            <Activity className="h-3 w-3" /> Llamadas hoy
                        </div>
                        <div className="font-display text-2xl mt-1">{usage.llamadas_hoy}</div>
                    </div>
                    <div className="kpi-card">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                            <TrendingUp className="h-3 w-3" /> Mes actual
                        </div>
                        <div className="font-display text-2xl mt-1 tabular">
                            ${usage.mes_actual.costo_usd?.toFixed(3) || "0.000"}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                            {usage.mes_actual.llamadas} llamadas
                        </div>
                    </div>
                    <div className="kpi-card">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                            <Zap className="h-3 w-3" /> Modelo
                        </div>
                        <div className="font-display text-lg mt-1 hash-cell">
                            {usage.por_modelo?.[0]?.model || "claude-sonnet-4-6"}
                        </div>
                    </div>
                </div>
            )}

            {/* KPIs cola */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hil-kpis">
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Pendientes</div>
                    <div className="font-display text-2xl mt-1">{kpis.pendiente}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">En revisión</div>
                    <div className="font-display text-2xl mt-1">{kpis.en_revision}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Prioridad alta</div>
                    <div className="font-display text-2xl mt-1 text-[color:var(--brand-coral)]">{kpis.alta}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">SLA excedido</div>
                    <div className={`font-display text-2xl mt-1 ${kpis.sla_excedido > 0 ? "text-[color:var(--brand-coral)]" : ""}`}>
                        {kpis.sla_excedido}
                    </div>
                </div>
            </div>

            <div className="flex gap-3 items-end flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Estado</div>
                    <Select value={filtroEstado || "todos"} onValueChange={(v) => set("estado", v === "todos" ? "" : v)}>
                        <SelectTrigger className="w-40" data-testid="hil-filter-estado"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="todos">Todos</SelectItem>
                            <SelectItem value="pendiente">Pendientes</SelectItem>
                            <SelectItem value="en_revision">En revisión</SelectItem>
                            <SelectItem value="resuelto">Resueltos</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Prioridad</div>
                    <Select value={filtroPrioridad || "todos"} onValueChange={(v) => set("prioridad", v === "todos" ? "" : v)}>
                        <SelectTrigger className="w-32" data-testid="hil-filter-prioridad"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="todos">Todas</SelectItem>
                            <SelectItem value="alta">Alta</SelectItem>
                            <SelectItem value="media">Media</SelectItem>
                            <SelectItem value="baja">Baja</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Tipo</div>
                    <Select value={filtroTipo || "todos"} onValueChange={(v) => set("tipo_revision", v === "todos" ? "" : v)}>
                        <SelectTrigger className="w-52" data-testid="hil-filter-tipo"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="todos">Todos</SelectItem>
                            <SelectItem value="baja_confianza">Baja confianza</SelectItem>
                            <SelectItem value="alto_stakes">Alto stakes</SelectItem>
                            <SelectItem value="flag_regulatorio">Flag PEP/PLD</SelectItem>
                            <SelectItem value="error_llm">Error LLM</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="hil-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th>ID</th>
                            <th>Tipo revisión</th>
                            <th>Estado</th>
                            <th>Motivo</th>
                            <th>Asignado a</th>
                            <th>SLA</th>
                            <th>Creado</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const p = PRIO_STYLE[r.prioridad] || PRIO_STYLE.baja;
                            const est = ESTADO_STYLE[r.estado] || ESTADO_STYLE.pendiente;
                            const Icon = est.icon;
                            const excedido = hoursAgo(r.fecha_creacion) > r.sla_horas && r.estado !== "resuelto";
                            return (
                                <tr key={r.id} data-testid={`hil-queue-row-${r.id}`}>
                                    <td>
                                        <div className={`h-3 w-3 rounded-full ${p.dot}`} title={p.label}></div>
                                    </td>
                                    <td className="hash-cell text-xs">{r.id.slice(0, 8)}…</td>
                                    <td>
                                        <span className="badge-gold text-xs">{TIPO_REV_LABEL[r.tipo_revision] || r.tipo_revision}</span>
                                    </td>
                                    <td>
                                        <span className={est.badge}>
                                            <Icon className="h-3 w-3" /> {est.label}
                                        </span>
                                    </td>
                                    <td className="text-xs text-muted-foreground max-w-[300px] truncate" title={r.motivo}>
                                        {r.motivo}
                                    </td>
                                    <td className="text-xs">
                                        {r.asignado_a || (
                                            <span className="text-muted-foreground italic">sin asignar</span>
                                        )}
                                    </td>
                                    <td className={`text-xs tabular ${excedido ? "text-[color:var(--brand-coral)] font-semibold" : ""}`}>
                                        {excedido && <AlertTriangle className="h-3 w-3 inline mr-1" />}
                                        {r.sla_horas}h
                                    </td>
                                    <td className="text-xs tabular">{r.fecha_creacion?.slice(0, 16)}</td>
                                    <td>
                                        <Link to={`/ia/hil/${r.id}`}>
                                            <Button size="sm" variant="ghost" data-testid={`hil-open-${r.id}`}>Abrir</Button>
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                        {rows.length === 0 && !loading && (
                            <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">
                                Sin items en la cola con estos filtros.
                            </td></tr>
                        )}
                        {loading && (
                            <tr><td colSpan={9} className="text-center py-6">
                                <Loader2 className="h-4 w-4 animate-spin inline" />
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
